import { WeatherModel } from "@/types";
import {
  AbstractModelSelectionPill,
  GenericModel,
} from "./AbstractModelSelectionPill";

interface WeatherModelSelectionPillProps {
  models?: WeatherModel[];
  selectedModel?: WeatherModel;
  onModelSelect?: (model: WeatherModel) => void;
  isLoading?: boolean;
}

const adaptWeatherModel = (model: WeatherModel): GenericModel => ({
  modelName: model.name || (model as any).weather_model_name,
  modelSlug: model.slug || (model as any).weather_model_slug,
  modelDescription: model.description || (model as any).weather_model_description || undefined,
});

export function WeatherModelSelectionPill({
  models = [],
  selectedModel,
  onModelSelect,
  isLoading = false,
}: WeatherModelSelectionPillProps) {
  if (isLoading) {
    return (
      <div className="px-2 py-1 rounded-md text-xs font-medium flex items-center bg-secondary text-secondary-foreground opacity-60">
        <span>Loading...</span>
      </div>
    );
  }

  const getAcronym = (model: WeatherModel): string => {
    const acronym = model.acronym || (model as any).weather_model_acronym;
    const name = model.name || (model as any).weather_model_name;
    
    if (acronym) return acronym;
    if (!name) return "UNK"; // Fallback if name is missing
    
    const match = name.match(/\(([^)]+)\)/);
    if (match) return match[1];
    const capitals = name.match(/[A-Z]/g);
    if (capitals && capitals.length > 1) return capitals.join("");
    return name.substring(0, 3).toUpperCase();
  };

  const getModelDisplayText = (modelName: string): string => {
    // Try to find by new name first, then fall back
    const model = models.find((m) => 
      (m.name === modelName) || ((m as any).weather_model_name === modelName)
    );
    if (!model) return modelName;
    return getAcronym(model);
  };

  const renderTooltipContent = (genericModel: GenericModel) => {
    const model = models.find(
      (m) => (m.name === genericModel.modelName) || ((m as any).weather_model_name === genericModel.modelName)
    );
    if (!model) return null;

    const name = model.name || (model as any).weather_model_name;
    const acronym = getAcronym(model);
    const description = model.description || (model as any).weather_model_description;
    const strengths = model.strengths || (model as any).weather_model_strengths;
    const weaknesses = model.weaknesses || (model as any).weather_model_weaknesses;

    return (
      <div className="space-y-2.5 max-w-[300px] text-sm">
        <h3 className="font-semibold border-b border-border pb-1.5">
          {name}
          {acronym &&
            !name.includes("(") &&
            ` (${acronym})`}
        </h3>

        {description && (
          <div className="space-y-1">
            <p className="font-medium text-primary">Description:</p>
            <p className="text-xs text-card-foreground/90 pl-1.5">
              {description}
            </p>
          </div>
        )}

        {strengths && (
          <div className="space-y-1">
            <p className="font-medium text-green-600">Strengths:</p>
            <p className="text-xs text-card-foreground/90 pl-1.5">
              {strengths}
            </p>
          </div>
        )}

        {weaknesses && (
          <div className="space-y-1">
            <p className="font-medium text-red-600">Weaknesses:</p>
            <p className="text-xs text-card-foreground/90 pl-1.5">
              {weaknesses}
            </p>
          </div>
        )}
      </div>
    );
  };

  const adaptedModels = models.map(adaptWeatherModel);
  const adaptedSelectedModel = selectedModel
    ? adaptWeatherModel(selectedModel)
    : undefined;

  const handleModelSelect = (adaptedModel: GenericModel) => {
    const originalModel = models.find(
      (model) => (model.name === adaptedModel.modelName) || ((model as any).weather_model_name === adaptedModel.modelName)
    );
    if (originalModel && onModelSelect) {
      onModelSelect(originalModel);
    }
  };

  return (
    <AbstractModelSelectionPill
      models={adaptedModels}
      selectedModel={adaptedSelectedModel}
      onModelSelect={handleModelSelect}
      labelPrefix="Weather Model:"
      dropdownTitle="Weather Model"
      renderTooltipContent={renderTooltipContent}
      showInfoIcon={true}
      getDisplayText={getModelDisplayText}
    />
  );
}
